<template>
	<aside v-if="hasContent" data-component="article-interview-block" class="article-interview-block">
		<article v-for="(segment, index) in normalizedSegments" :key="segment.key || index" class="interview-segment">
			<div v-if="segment.hasAuthorMeta" class="author-meta">
				<div v-if="segment.authorImage" class="author-image-wrapper">
					<ImageAsset :asset="segment.authorImage" class="author-image" />
				</div>
				<div class="author-copy">
					<p v-if="segment.authorName" class="-body-big-medium author-name">{{ segment.authorName }}</p>
				</div>
			</div>
			<p v-if="segment.text" class="-body-big-regular interview-text">{{ segment.text }}</p>
		</article>
	</aside>
</template>

<script setup>
import { ImageAsset } from '~/components/core'

const props = defineProps({
	value: { type: Object, default: undefined },
	segments: { type: Array, default: undefined },
})

function getAuthorImage(person) {
	const media = Array.isArray(person?.media) ? person.media : []
	return media.find((item) => item?.type === 'image' && item?.url) || null
}

const sourceSegments = computed(() => {
	if (Array.isArray(props.value?.segments)) return props.value.segments
	if (Array.isArray(props.segments)) return props.segments
	return []
})

const normalizedSegments = computed(() => {
	return sourceSegments.value
		.map((segment, index) => {
			const text = typeof segment?.text === 'string' ? segment.text.trim() : ''
			const person = segment?.person || {}
			const authorName = typeof person?.name === 'string' ? person.name.trim() : ''
			const authorRole = typeof person?.role === 'string' ? person.role.trim() : ''
			const authorImage = getAuthorImage(person)
			const hasAuthorMeta = Boolean(authorImage || authorName || authorRole)

			return {
				key: segment?._key || `${index}-${text.slice(0, 24)}`,
				text,
				authorName,
				authorRole,
				authorImage,
				hasAuthorMeta,
			}
		})
		.filter((segment) => Boolean(segment.text || segment.hasAuthorMeta))
})

const hasContent = computed(() => normalizedSegments.value.length > 0)
</script>

<style lang="scss" scoped>
[data-component='article-interview-block'] {
	margin: rs(56, 56) 0;
	margin-bottom: rs(30, 30);
	display: flex;
	flex-direction: column;
	gap: rs(12, 12);

	.interview-segment {
		display: flex;
		flex-direction: column;
		gap: rs(12, 12);
	}

	.interview-text {
		margin: 0;
		color: var(--color-neutral-tints-700);
		padding-left: rs(24, 24);
		padding-bottom: rs(24, 24);
		margin-left: rs(12, 12);
		border-left: 1px solid var(--color-neutral-tints-100);

		white-space: pre-line;
	}

	.author-meta {
		display: flex;
		align-items: center;
		gap: rs(12, 12);
	}

	.author-image-wrapper {
		width: rs(24, 24);
		height: rs(24, 24);
		border-radius: 50%;
		overflow: hidden;
		flex: 0 0 auto;
	}

	.author-image {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.author-copy {
		display: flex;
		flex-direction: column;
		gap: rs(2, 2);
	}

	.author-name,
	.author-role {
		margin: 0;
		color: var(--color-river-blue-900);
	}

	.author-role {
		color: var(--color-river-blue-500);
	}
}
</style>
